import BookButton from "@/components/ui/BookButton";
import Image from "next/image";
import React from "react";
import { IoSparklesSharp } from "react-icons/io5";
import bg from "@/public/bg.webp";
import { Inter } from "next/font/google";
import ShimmerText from "@/pages/new-home/_components/ShimmerText";

const inter = Inter({
  subsets: ["latin"],
  display: "swap",
});

const colorTheme = {
  slate: {
    backgroundGradient: "from-slate-50 via-white to-slate-50",
    textGradient: "from-[#94A3B8] via-[#475569] to-[#1E293B]",
    primaryColor: "text-slate-500",
  },
  gray: {
    backgroundGradient: "from-gray-50 via-white to-gray-50",
    textGradient: "from-[#9CA3AF] via-[#4B5563] to-[#1F2937]",
    primaryColor: "text-gray-500",
  },
  zinc: {
    backgroundGradient: "from-zinc-50 via-white to-zinc-50",
    textGradient: "from-[#A1A1AA] via-[#52525B] to-[#27272A]",
    primaryColor: "text-zinc-500",
  },
  neutral: {
    backgroundGradient: "from-neutral-50 via-white to-neutral-50",
    textGradient: "from-[#A3A3A3] via-[#525252] to-[#262626]",
    primaryColor: "text-neutral-500",
  },
  stone: {
    backgroundGradient: "from-stone-50 via-white to-stone-50",
    textGradient: "from-[#A8A29E] via-[#57534E] to-[#292524]",
    primaryColor: "text-stone-500",
  },
  red: {
    backgroundGradient: "from-red-50 via-white to-red-50",
    textGradient: "from-[#F87171] via-[#EF4444] to-[#B91C1C]",
    primaryColor: "text-red-500",
  },
  orange: {
    backgroundGradient: "from-orange-50 via-white to-orange-50",
    textGradient: "from-[#FB923C] via-[#F97316] to-[#C2410C]",
    primaryColor: "text-orange-500",
  },
  amber: {
    backgroundGradient: "from-amber-50 via-white to-amber-50",
    textGradient: "from-[#FBBF24] via-[#D97706] to-[#92400E]",
    primaryColor: "text-amber-500",
  },
  yellow: {
    backgroundGradient: "from-yellow-50 via-white to-yellow-50",
    textGradient: "from-[#FDE047] via-[#FACC15] to-[#CA8A04]",
    primaryColor: "text-yellow-500",
  },
  lime: {
    backgroundGradient: "from-lime-50 via-white to-lime-50",
    textGradient: "from-[#A3E635] via-[#65A30D] to-[#3F6212]",
    primaryColor: "text-lime-500",
  },
  green: {
    backgroundGradient: "from-green-50 via-white to-green-50",
    textGradient: "from-[#4ADE80] via-[#22C55E] to-[#166534]",
    primaryColor: "text-green-500",
  },
  emerald: {
    backgroundGradient: "from-emerald-50 via-white to-emerald-50",
    textGradient: "from-[#34D399] via-[#10B981] to-[#065F46]",
    primaryColor: "text-emerald-500",
  },
  teal: {
    backgroundGradient: "from-teal-50 via-white to-teal-50",
    textGradient: "from-[#2DD4BF] via-[#14B8A6] to-[#0F766E]",
    primaryColor: "text-teal-500",
  },
  cyan: {
    backgroundGradient: "from-cyan-50 via-white to-cyan-50",
    textGradient: "from-[#22D3EE] via-[#06B6D4] to-[#0E7490]",
    primaryColor: "text-cyan-500",
  },
  sky: {
    backgroundGradient: "from-sky-50 via-white to-sky-50",
    textGradient: "from-[#38BDF8] via-[#0284C7] to-[#075985]",
    primaryColor: "text-sky-500",
  },
  blue: {
    backgroundGradient: "from-blue-50 via-white to-blue-50",
    textGradient: "from-[#049FF2] via-[#0A65F1] to-[#0629C8]",
    primaryColor: "text-blue-500",
  },
  indigo: {
    backgroundGradient: "from-indigo-50 via-white to-indigo-50",
    textGradient: "from-[#818CF8] via-[#6366F1] to-[#4338CA]",
    primaryColor: "text-indigo-500",
  },
  violet: {
    backgroundGradient: "from-violet-50 via-white to-violet-50",
    textGradient: "from-[#A78BFA] via-[#8B5CF6] to-[#4C1D95]",
    primaryColor: "text-violet-500",
  },
  purple: {
    backgroundGradient: "from-purple-50 via-white to-purple-50",
    textGradient: "from-[#C084FC] via-[#A855F7] to-[#6B21A8]",
    primaryColor: "text-purple-500",
  },
  fuchsia: {
    backgroundGradient: "from-fuchsia-50 via-white to-fuchsia-50",
    textGradient: "from-[#E879F9] via-[#D946EF] to-[#86198F]",
    primaryColor: "text-fuchsia-500",
  },
  pink: {
    backgroundGradient: "from-pink-50 via-white to-pink-50",
    textGradient: "from-[#F472B6] via-[#EC4899] to-[#BE185D]",
    primaryColor: "text-pink-500",
  },
  rose: {
    backgroundGradient: "from-rose-50 via-white to-rose-50",
    textGradient: "from-[#FB7185] via-[#F43F5E] to-[#BE123C]",
    primaryColor: "text-rose-500",
  },
};



const ServiceHero = ({
  subtitle,
  title,
  highlightedText,
  description,
  buttonText = "Book a Free Consultation",
  color = "blue",
  children,
}) => {
  // Helper function to render title with highlighted text
  const renderTitle = () => {
    if (!title) return null;

    if (highlightedText && title.includes(highlightedText)) {
      const parts = title.split(highlightedText);
      return (
        <>
          {parts[0]}
          <span className={`bg-gradient-to-br ${currentTheme.textGradient} bg-clip-text font-extrabold text-transparent lg:text-7xl`}>
            {highlightedText}
          </span>
          {parts[1]}
        </>
      );
    }    return title;
  };

  // Get current theme or fallback to blue
  const currentTheme = colorTheme[color] || colorTheme.blue;


  return (
    <section className={`relative overflow-hidden bg-gradient-to-br ${currentTheme.backgroundGradient} py-16`}>
      <div className="wrapper relative z-10 text-left">
        <div>
          {subtitle && (
            <h2
              className="mb-5 justify-center gap-2.5 text-center font-extrabold uppercase lg:text-xl"
              data-aos="fade-in"
            >
              <IoSparklesSharp className={`mr-2 inline-block ${currentTheme.primaryColor}`} />
              <span className={`bg-gradient-to-br ${currentTheme.textGradient} bg-clip-text text-transparent`}>
                {subtitle}
              </span>
            </h2>
          )}

          <h1
            className="relative z-10 mx-auto max-w-screen-lg text-center text-3xl font-extrabold !leading-tight tracking-tight sm:text-4xl lg:text-7xl"
            data-aos="fade-in"
            data-aos-delay="100"
          >
            <ShimmerText />
            {renderTitle()}
          </h1>

          {description && (
            <p
              className={`relative z-10 mx-auto mt-5 max-w-4xl text-center !leading-normal subpixel-antialiased lg:text-2xl ${inter.className}`}
              data-aos="fade-in"
              data-aos-delay="200"
            >
              {description}
            </p>
          )}

          <div
            className="relative z-10 mt-10 flex justify-center"
            data-aos="fade-in"
            data-aos-delay="300"
          >
            <BookButton
              color={color}
              text={buttonText}
              custom="text-center"
            />
          </div>
        </div>

        <div
          id="section-hero-content"
          className="mt-16 w-full select-none"
          data-aos-delay="300"
          data-aos="fade-up"
        >
          {children}
        </div>
      </div>
    </section>
  );
};

export default ServiceHero;
